import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  DialogContent,
  Divider,
  Grid,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableHead,
  TableRow,
  Link,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { getClientSecretForSMSCredits } from 'wisestep-api';
import {
  ControlledTextInput,
  WsButton,
  WsColors,
  WsDialog,
  WsTypoColors,
  WsTypography,
  WsTypoVariants,
} from 'wisestep-base';

import { useWorkspaceContext } from '../../../context';
import TrustBadge from '../../../images/content/stripe-badge-transparent.png';
import { failed } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import PaymentContext from '../../paymentFlow/components/PaymentContext';

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: WsColors.TertiaryShades100,
    color: WsColors.GenericRichBlack,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const AlingTextSideBySide = ({
  leftText,
  rightText,
  leftTextCaption,
}: {
  leftText: string;
  rightText: string;
  leftTextCaption?: string;
}) => (
  <Grid container mt={2}>
    <Grid item xs={6} textAlign="left">
      <WsTypography bold> {leftText}</WsTypography>
      {leftTextCaption && (
        <WsTypography bold variant={WsTypoVariants.BODY2} color={WsTypoColors.QUINARY}>
          {leftTextCaption}
        </WsTypography>
      )}
    </Grid>
    <Grid item xs={6} textAlign="right">
      <WsTypography bold>{rightText}</WsTypography>
    </Grid>
    <Divider sx={{ width: '100%', mt: 1 }} />
  </Grid>
);

const RechargeCommunicationCredits = ({ onClose }: { onClose: () => void }) => {
  const workspace = useWorkspaceContext();
  const totalPrice = 10;
  const [secretKey, setSecretKey] = useState<string | null>(null);
  const [totalCharge, setTotalCharge] = useState<number>(10);
  const setNotification = useNotificationStore((state) => state.setNotification);
  const methods = useForm({
    defaultValues: {
      users: 1,
    },
  });

  const { watch } = methods;

  const totalUsers = watch('users');

  useEffect(() => {
    if (totalUsers > 0) {
      setTotalCharge(totalUsers * totalPrice);
    }
  }, [totalUsers]);

  const onSubmit = async () => {
    try {
      const res = await getClientSecretForSMSCredits({
        amount: totalCharge,
        quantity: String(totalUsers * 1000),
      });
      setSecretKey(res.data);
    } catch (e) {
      setNotification(failed('Something went wrong please try later'));
    }
  };

  return (
    <WsDialog
      size={workspace.razorpaySubscriptionId ? 'small' : 'medium'}
      openDialog={true}
      onClose={onClose}
    >
      <DialogContent>
        <CloseIcon sx={{ float: 'right' }} onClick={onClose} />
        {workspace.razorpaySubscriptionId ? (
          <Grid container textAlign="center">
            <Grid item xs={12}>
              <WsTypography variant={WsTypoVariants.TITLE} bold>
                For Business registered in India
              </WsTypography>
            </Grid>
            <Grid item xs={12} mt={1}>
              <WsTypography variant={WsTypoVariants.BODY1}>
                Please contact our customer success team for SMS credits
              </WsTypography>
              <Link
                underline="none"
                sx={{ color: WsColors.TheBlues500, fontWeight: 500 }}
                href="mailto:customersuccess@wisestep-inc.com"
              >
                customersuccess@wisestep-inc.com
              </Link>
            </Grid>
            <Grid item xs={12} textAlign="center" mt={2}>
              <WsButton onClick={onClose} variant="outlined">
                Close
              </WsButton>
            </Grid>
          </Grid>
        ) : (
          <>
            {' '}
            {secretKey ? (
              <PaymentContext secretKey={secretKey} showPayButton={true} updateUser={true} />
            ) : (
              <Grid container>
                <FormProvider {...methods}>
                  <Grid item xs={12} textAlign="center">
                    <WsTypography variant={WsTypoVariants.TITLE} color={WsTypoColors.PRIMARY} bold>
                      Your Cart
                    </WsTypography>
                  </Grid>
                  <Grid item xs={12} mt={3}>
                    <TableContainer>
                      <Table sx={{ minWidth: 700 }} aria-label="customized table">
                        <TableHead>
                          <TableRow>
                            <StyledTableCell>
                              <WsTypography bold>S.no</WsTypography>
                            </StyledTableCell>
                            <StyledTableCell>
                              <WsTypography bold>Plan</WsTypography>
                            </StyledTableCell>
                            <StyledTableCell>
                              <WsTypography bold>$10 per 1000 Credits</WsTypography>
                            </StyledTableCell>
                            <StyledTableCell>
                              <WsTypography bold>Quantity</WsTypography>
                            </StyledTableCell>
                            <StyledTableCell>
                              <WsTypography bold>Total</WsTypography>
                            </StyledTableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <TableRow>
                            <TableCell component="th" scope="row">
                              <WsTypography bold color={WsTypoColors.QUINARY}>
                                1.
                              </WsTypography>
                            </TableCell>
                            <TableCell component="th" scope="row">
                              <WsTypography bold color={WsTypoColors.QUINARY}>
                                Recharge SMS Credits
                              </WsTypography>
                              <WsTypography bold color={WsTypoColors.QUINARY}>
                                {`(${totalUsers * 1000} Credits)`}
                              </WsTypography>
                            </TableCell>
                            <TableCell component="th" scope="row">
                              <WsTypography bold color={WsTypoColors.QUINARY}>
                                $ 10
                              </WsTypography>
                            </TableCell>
                            <TableCell component="th" scope="row">
                              <ControlledTextInput
                                name="users"
                                labelText=""
                                type="number"
                                fullWidth
                                id="users"
                                placeholder=""
                                InputProps={{ inputProps: { min: 1 } }}
                                variant="outlined"
                                autoComplete="users"
                                sx={{ width: 150 }}
                              />
                            </TableCell>
                            <TableCell component="th" scope="row">
                              <WsTypography bold color={WsTypoColors.QUINARY}>
                                {`$ ${totalCharge}`}
                              </WsTypography>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ mt: '37%' }}>
                      <img
                        src={TrustBadge}
                        alt="Guaranteed safe & secure checkout"
                        style={{ height: '38px' }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={2}></Grid>
                  <Grid item xs={4}>
                    <AlingTextSideBySide leftText="Subtotal:" rightText={`$ ${totalCharge}`} />
                    <AlingTextSideBySide leftText="Sales Tax:" rightText={`$ 0`} />
                    <AlingTextSideBySide leftText="Grand Total:" rightText={`$ ${totalCharge}`} />
                    <Stack mt={2}>
                      <WsButton size="large" disabled={totalUsers == 0} onClick={onSubmit}>
                        Pay Now
                      </WsButton>
                    </Stack>
                  </Grid>
                </FormProvider>
              </Grid>
            )}
          </>
        )}
      </DialogContent>
    </WsDialog>
  );
};

export default RechargeCommunicationCredits;
